/**
 * use-nav-context — arma el `NavContext` del nav-model REACTIVAMENTE:
 *
 *   - sesión per-tab (`useSession`): logged/guest + rol de plataforma (F2-C);
 *   - rol en la org ACTIVA (`useActiveOrg` + `useMyOrgRole`): un switch de org
 *     o de cuenta re-scopea la visibilidad sin recargar;
 *   - guardian (filas guardian_link propias) y teacher (filas cohort_teacher
 *     propias, sincronizadas por el stream `my_cohorts_teacher`) — local-first.
 *
 * Todo son señales/queries reactivas: la sidebar re-evalúa la matriz al vuelo.
 */
import { createMemo } from "solid-js";
import { cohort_teacher, eq } from "@space/powersync";
// FLAGS: gating de items por feature (org activa) — mismo resolutor que rutas.
import {
  cohortQueryKeys,
  featureGraph,
  parseFeatureOverrides,
  resolveFeatureEffective,
} from "@space/shared";
import { createPowerSyncSingle } from "@space/shared/solid";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { useMyGuardianWards, useMyOrgRole } from "@/features/academy/hooks/use-people-data";
import { useOrgSettings } from "@/features/academy/hooks/use-academy-data";
import { usePlatformFlags } from "@/features/flags/use-feature-enabled";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import type { NavContext } from "./nav-model";

export function useNavContext(): () => NavContext {
  const session = useSession();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { activeOrgId } = useActiveOrg();
  const myOrgRole = useMyOrgRole(activeOrgId);
  const wards = useMyGuardianWards();

  // ¿Tengo filas cohort_teacher propias? (docente asignado a alguna cohorte,
  // aunque su member.role no sea `teacher`). Basta con 1 fila.
  const teacherRow = createPowerSyncSingle<{ id: string }>({
    queryKey: () => cohortQueryKeys.myTeaching(session.userId()),
    db,
    queryFn: () => {
      const d = drizzle();
      const uid = session.userId();
      return d && uid
        ? d
            .select({ id: cohort_teacher.id })
            .from(cohort_teacher)
            .where(eq(cohort_teacher.user_id, uid))
            .limit(1)
        : null;
    },
    enabled: () => Boolean(session.userId()) && Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  // FLAGS: resolutor efectivo (grafo incluido) sobre flags + org activa.
  const platformFlags = usePlatformFlags();
  const orgSettings = useOrgSettings(() => activeOrgId());
  const graph = featureGraph();
  const featureEnabled = createMemo(() => {
    const platformMap = platformFlags();
    const org = parseFeatureOverrides(orgSettings.data?.features);
    return (key: string): boolean =>
      resolveFeatureEffective(key, { platformMap, org }, graph).enabled;
  });

  const context = createMemo<NavContext>(() => {
    const isLoggedIn = session.isAuthenticated();
    return {
      isLoggedIn,
      featureEnabled: featureEnabled(),
      platformRole: session.data?.user.role ?? null,
      // Sin sesión real no hay rol de org (un invitado jamás hereda la org del
      // usuario global). `useMyOrgRole` normaliza membresía ausente → student,
      // que no abre ninguna sección.
      orgRole: isLoggedIn ? myOrgRole.role() : null,
      isTeacher: isLoggedIn && (myOrgRole.role() === "teacher" || Boolean(teacherRow.data)),
      isGuardian: isLoggedIn && (wards.data?.length ?? 0) > 0,
    };
  });
  return context;
}
